// src/components/ManaStarMeter.js
import React, { useEffect, useMemo, useRef } from "react";

/**
 * ManaStarMeter
 * A vertical mana cylinder with rising liquid. A star above unlocks at 100%.
 *
 * props:
 *  - value: number (current progress)
 *  - max: number (total steps)              default 7
 *  - height: number (px)                    default 320
 *  - width: number (px)                     default 56
 *  - showLabel: boolean                     default false
 *  - onComplete: () => void                 called once when value reaches max
 */
export default function ManaStarMeter({
  value = 0,
  max = 7,
  height = 320,
  width = 56,
  showLabel = false,
  onComplete,
  className = "",
}) {
  const pct = useMemo(
    () => Math.max(0, Math.min(100, max ? (value / max) * 100 : 0)),
    [value, max]
  );
  const complete = pct >= 100 - 1e-6;
  const firedRef = useRef(false);

  useEffect(() => {
    if (complete && !firedRef.current) {
      firedRef.current = true;
      onComplete?.();
    }
  }, [complete, onComplete]);

  return (
    <div className={`flex flex-col items-center ${className}`} style={{ width }}>
      {/* ⭐ Star badge */}
      <div
        className={`relative mb-2 transition-transform duration-500 ${
          complete ? "animate-[pop_550ms_ease-out]" : ""
        }`}
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 100 100"
          width={Math.max(54, width)}
          height={Math.max(54, width)}
          className="drop-shadow"
        >
          {/* glow when complete */}
          {complete && (
            <circle
              cx="50"
              cy="50"
              r="30"
              fill="url(#glowGrad)"
              opacity="0.55"
            />
          )}
          {/* star shape */}
          <defs>
            <linearGradient id="starGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#fde68a" />
              <stop offset="100%" stopColor="#f59e0b" />
            </linearGradient>
            <radialGradient id="glowGrad">
              <stop offset="0%" stopColor="#fef3c7" />
              <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
            </radialGradient>
          </defs>
          <path
            d="M50 6 L61 37 L94 37 L66 56 L76 87 L50 69 L24 87 L34 56 L6 37 L39 37 Z"
            fill={complete ? "url(#starGrad)" : "none"}
            stroke={complete ? "#f59e0b" : "#bdbdbd"}
            strokeWidth="4"
            opacity={complete ? 1 : 0.5}
          />
        </svg>

        {/* small lock icon when not complete */}
        {!complete && (
          <span className="absolute -right-1 -top-1 text-[12px] text-gray-400">🔒</span>
        )}
      </div>

      {/* Cylinder */}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        title={`${Math.round(pct)}%`}
        className="relative overflow-hidden rounded-[18px] border border-blue-200 bg-white/60 shadow-inner"
        style={{ width, height }}
      >
        {/* glass edge highlight */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-white/60 to-transparent" />

        {/* liquid */}
        <div
          className="absolute bottom-0 left-0 right-0 transition-all duration-500 ease-out"
          style={{
            height: `${pct}%`,
            background:
              "linear-gradient(180deg, rgba(147,197,253,1) 0%, rgba(37,99,235,1) 100%)",
            boxShadow: "inset 0 8px 14px rgba(255,255,255,.25)",
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
          }}
        />

        {/* bubbles (respect motion reduce via CSS) */}
        <div className="absolute inset-x-0 bottom-0 h-full pointer-events-none overflow-hidden">
          <div className="bubble left-2" />
          <div className="bubble left-1/2" style={{ animationDuration: "3.2s" }} />
          <div className="bubble right-3" style={{ animationDuration: "2.4s" }} />
          <div className="bubble right-5" style={{ animationDuration: "3.6s" }} />
        </div>
      </div>

      {showLabel && (
        <div className="mt-2 text-xs font-semibold text-blue-700">
          {value} / {max}
        </div>
      )}

      {/* styles */}
      <style>{`
        .bubble {
          position:absolute; bottom:6px; width:8px; height:8px;
          background: rgba(255,255,255,.65); border-radius:9999px;
          animation: rise 2.8s linear infinite;
        }
        @keyframes rise {
          0% { transform: translateY(0) translateX(0); opacity:0; }
          15% { opacity:.9; }
          100% { transform: translateY(-120%) translateX(-10%); opacity:0; }
        }
        @keyframes pop {
          0% { transform: scale(.7); }
          60% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .bubble { animation: none; }
        }
      `}</style>
    </div>
  );
}
